import RefreshButton from '@/components/RefreshButton';
import { PAY_TYPE_OPTIONS } from '@/app/features/employee/emp-payroll';
import EmptyState from '@/components/EmptyState';
import OfficeSelect from '@/components/OfficeSelect';
import { useAppDiscloser, useSelectionOptions } from '@/hooks';
import {
  ChevronDownIcon,
  CirclePlusIcon,
  DownloadIcon,
  EyeIcon,
  PencilEdit02Icon,
  SearchIcon,
  UploadIcon,
  UsersIcon,
} from '@/icons';
import { Button, Flex, Input, Select, Space, type TableProps } from 'antd';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from '@/lib/toast';
import dayjs from 'dayjs';
import {
  useGetAllDepartmentsQuery,
  useGetAllDesignationsQuery,
  useGetAllEmployeesQuery,
  useLazyExportEmployeesQuery,
} from '../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import type { AllEmployeeParams } from '../../../../app/features/employee/emp-dashboard/emp-dashboard.type';
import IMAGES from '../../../../assets/images';
import CustomImage from '../../../../components/CustomImage';
import CustomPagination from '../../../../components/CustomPagination';
import CustomTable from '../../../../components/CustomTable';
import StatusTag from '../../../../components/StatusTag';
import ViewToggler from '../../../../components/ViewToggler';
import useAuth from '../../../../hooks/useAuth';
import useFilters from '../../../../hooks/useFilters';
import useModal from '../../../../hooks/useModal';
import { useStoredState } from '../../../../hooks/useStoredState';
import BulkEmployeeUpload from './_components/BulkEmployeeUpload/BulkEmployeeUpload';

const EMPLOYEE_STATUS_CONFIG = [
  { label: 'Active', value: 'active', className: 'status status-success' },
  { label: 'Inactive', value: 'inactive', className: 'status status-error' },
];

const EmployeeDirectory = () => {
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const [mode, setMode] = useStoredState('emp-view', 'grid');
  const [, handleAddEmployeeOpen] = useAppDiscloser('CREATE_EMPLOYEE');
  const {
    open: bulkUploadOpen,
    handleOpen: handleBulkUploadOpen,
    handleClose: handleBulkUploadClose,
  } = useModal<undefined, 'BULK_UPLOAD'>();

  const handleAddEmployee = () => {
    handleAddEmployeeOpen({ type: 'ADD' });
  };

  const handleBulkUpload = () => {
    handleBulkUploadOpen({ type: 'BULK_UPLOAD' });
  };

  // Use filters hook
  const {
    page,
    limit,
    filter,
    keyword,
    handleSearch,
    handleFilter,
    handlePagination,
    resetPagination,
  } = useFilters<AllEmployeeParams>({
    defaultLimit: mode === 'grid' ? 12 : 10,
  });

  // Get departments and designations for filter dropdowns
  const [departmentOptions] = useSelectionOptions(useGetAllDepartmentsQuery, {
    labelKey: 'name',
    valueKey: 'id',
    params: { params: { page: 1, limit: 100, status: 'active' } },
  });

  const [designationOptions] = useSelectionOptions(useGetAllDesignationsQuery, {
    labelKey: 'name',
    valueKey: 'id',
    params: { params: { page: 1, limit: 100 } },
  });

  const {
    data: employeesData,
    isLoading: employeesLoading,
    isFetching: employeesFetching,
  } = useGetAllEmployeesQuery({
    params: {
      ...filter,
      page,
      limit,
      tenant_id: tenantId,
      search: keyword || undefined,
    },
  });

  // Use API data directly
  const employees = employeesData?.data?.data || [];

  const [exportEmployees, { isFetching: exporting }] =
    useLazyExportEmployeesQuery();

  // Exports every employee matching the filters on screen (not just one page).
  const handleExport = async () => {
    try {
      const blobUrl = await exportEmployees({
        department_id: filter?.department_id,
        designation_id: filter?.designation_id,
        office_id: filter?.office_id,
        status: filter?.status,
        employee_type: filter?.employee_type,
        search: keyword || undefined,
      }).unwrap();
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `employees_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', 'Failed to export employees.');
    }
  };

  const columns: TableProps<Employee>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'employee_id',
      // align: "center",
      render: (val: string) => val || 'N/A',
    },
    {
      title: 'Employee',
      dataIndex: 'name',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <CustomImage
            size={40}
            className="rounded-full object-cover"
            src={record.photo || IMAGES.avatar}
          />
          <div>
            <div className="font-medium">{record.name}</div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },

    {
      title: 'Designation',
      dataIndex: 'designation',
      render: (designation) => designation?.name ?? 'n/a',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      render: (department) => department?.name ?? 'n/a',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={EMPLOYEE_STATUS_CONFIG} />
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      align: 'center',
      render: (id: string) => (
        <Space>
          <Link to={`employee-details/${id}`}>
            <Button
              size="small"
              className="action-btn"
              icon={<EyeIcon size={20} className="text-[22px]" />}
            />
          </Link>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (mode) {
      resetPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <>
      <div>
        <div className="space-y-6 filter-section mb-6">
          <div>
            <div className="flex gap-2 justify-end flex-wrap">
              <Input
                size="large"
                onChange={handleSearch}
                className="shrink-0 w-[225px]"
                placeholder="Search Employee ID"
                prefix={<SearchIcon size={24} />}
              />
              <div>
                <Select
                  size="large"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Department"
                  placement="bottomRight"
                  popupMatchSelectWidth={false}
                  suffixIcon={<ChevronDownIcon size={24} />}
                  onChange={(value) => handleFilter('department_id', value)}
                  allowClear
                  options={departmentOptions}
                />
              </div>
              <div>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  size="large"
                  placeholder="Designation"
                  placement="bottomRight"
                  popupMatchSelectWidth={false}
                  suffixIcon={<ChevronDownIcon size={24} />}
                  onChange={(value) => handleFilter('designation_id', value)}
                  options={designationOptions}
                />
              </div>
              <div>
                <OfficeSelect
                  allowClear
                  officeStatus="ALL"
                  size="large"
                  placeholder="Office"
                  placement="bottomRight"
                  popupMatchSelectWidth={false}
                  onChange={(value) => handleFilter('office_id', value)}
                />
              </div>
              <Select
                size="large"
                placeholder="Status"
                placement="bottomRight"
                style={{ width: 130 }}
                styles={{ popup: { root: { width: 130 } } }}
                suffixIcon={
                  <ChevronDownIcon className="text-white" size={24} />
                }
                onChange={(value) => handleFilter('status', value)}
                allowClear
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
              />
              <Select
                size="large"
                placeholder="Type"
                placement="bottomRight"
                style={{ width: 130 }}
                styles={{ popup: { root: { width: 150 } } }}
                suffixIcon={
                  <ChevronDownIcon className="text-white" size={24} />
                }
                onChange={(value) => handleFilter('employee_type', value)}
                allowClear
                options={[
                  { value: 'full-time', label: 'Full Time' },
                  { value: 'part-time', label: 'Part Time' },
                  { value: 'contract', label: 'Contract' },
                ]}
              />
              <Select
                size="large"
                placeholder="Pay Basis"
                placement="bottomRight"
                style={{ width: 130 }}
                styles={{ popup: { root: { width: 130 } } }}
                suffixIcon={
                  <ChevronDownIcon className="text-white" size={24} />
                }
                onChange={(value) => handleFilter('pay_basis', value)}
                allowClear
                options={PAY_TYPE_OPTIONS}
              />
            </div>
          </div>
          <Flex align="center" justify="space-between" wrap>
            <ViewToggler mode={mode} setMode={setMode} />

            <Flex gap={8}>
              <RefreshButton
                endpoints={[
                  'getAllEmployees',
                  'getFailedEmployeeImportJobs',
                ]}
              />
              <Button
                size="large"
                className="btn-primary-bordered !h-10"
                icon={<DownloadIcon size={20} />}
                loading={exporting}
                onClick={handleExport}
              >
                Export
              </Button>
              <Button
                size="large"
                type="default"
                className="btn-primary"
                onClick={handleBulkUpload}
              >
                Upload CSV
                <UploadIcon size={20} className="!text-[#444444] !font-bold" />
              </Button>
              <Button
                size="large"
                className="btn-primary"
                onClick={handleAddEmployee}
              >
                <CirclePlusIcon size={20} />
                Add New Employee
              </Button>
            </Flex>
          </Flex>
        </div>

        {mode === 'grid' ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {employeesLoading || employeesFetching ? (
                // Loading skeleton for grid view
                Array.from({ length: 8 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-theme-slate-800 rounded-xl p-4 animate-pulse"
                  >
                    <Flex justify="space-between" gap={16}>
                      <div className="w-[70px] h-[70px] bg-theme-slate-700 rounded-full" />
                      <div className="w-8 h-8 bg-theme-slate-700 rounded-full" />
                    </Flex>
                    <div className="mt-5 space-y-3">
                      <div className="h-6 bg-theme-slate-700 rounded w-3/4" />
                      <div className="h-4 bg-theme-slate-700 rounded w-1/2" />
                      <div className="h-4 bg-theme-slate-700 rounded w-2/3" />
                    </div>
                  </div>
                ))
              ) : employees.length > 0 ? (
                employees.map((item: Employee) => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`employee-details/${item.id}`)}
                    className="bg-theme-slate-800 rounded-xl p-4 hover:outline hover:outline-2 hover:outline-white cursor-pointer"
                  >
                    <Flex justify="space-between" gap={16}>
                      {/* <div className="w-[70px] h-[70px] rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-gray-600 font-medium text-xl">
                          {firstChar(item.name)}
                        </span>
                      </div> */}
                      <CustomImage
                        size={70}
                        className="rounded-full object-cover"
                        src={item?.photo || IMAGES.avatar}
                      />

                      <Button
                        size="small"
                        className="btn-action !rounded-full dark:hover:!bg-theme-slate-700 dark:!bg-theme-slate-700"
                        classNames={{
                          icon: 'flex items-center justify-center',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`employee-details/${item.id}/manage`);
                        }}
                        icon={<PencilEdit02Icon size={20} />}
                      />
                    </Flex>

                    <div className="mt-5">
                      <div className="flex justify-start items-center gap-4">
                        <div className="text-white text-xl font-black">
                          {item.name}
                        </div>
                        <div>
                          <StatusTag
                            value={item?.status ?? '-'}
                            config={EMPLOYEE_STATUS_CONFIG}
                          />
                        </div>
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        {item.email}
                      </div>
                    </div>

                    <div className="flex justify-start items-center gap-2 mt-4">
                      <div className="text-white text-base font-normal">
                        ID :
                      </div>

                      <div className="text-white text-lg font-bold">
                        {item?.employee_id}
                      </div>
                    </div>

                    <p className="mt-3 mb-[10px] text-base text-gray-300">
                      {item.designation?.name || 'No designation'}
                    </p>

                    <Flex
                      align="flex-start"
                      className="text-white text-base"
                      gap={8}
                    >
                      Department:{' '}
                      <div className="text-xl text-white font-black">
                        {item.department?.name || 'No department'}
                      </div>
                    </Flex>
                  </div>
                ))
              ) : (
                // Empty state for grid view
                <div className="col-span-full">
                  <EmptyState
                    icon={<UsersIcon size={40} />}
                    title="No Employees Found"
                    description="There are no employees in the directory yet. Start by adding your first employee to get started."
                  />
                </div>
              )}
            </div>

            {/* Pagination for grid mode */}
            <CustomPagination
              total={employeesData?.data?.totalLength || 0}
              limit={limit}
              page={page}
              handlePagination={handlePagination}
            />
          </>
        ) : (
          <>
            <CustomTable
              page={page}
              limit={limit}
              hideRowSelect
              columns={columns}
              dataSource={employees}
              onPaginate={handlePagination}
              tableRootClassName="!border-0 !p-0"
              customTitle="Employee List"
              loading={employeesLoading || employeesFetching}
              total={employeesData?.data?.totalLength || 0}
            />
          </>
        )}
      </div>


      {/* Bulk Employee Upload Drawer */}
      <BulkEmployeeUpload
        data={null}
        open={bulkUploadOpen.open}
        handleClose={handleBulkUploadClose}
      />
    </>
  );
};

export default EmployeeDirectory;

export interface Employee {
  id: string;
  name: string;
  photo: string | null;
  email: string;
  department: null | {
    id: string;
    name: string;
  };
  designation: {
    id: string;
    name: string;
  };
  employee_id: string;
  status?: string;
}
